import { createAppSelector } from '../../app/createAppSelector';
import {
    getHardwareDetailGroup,
    getTariffWithHardware,
} from '../../api/glados';
import {
    selectCurrentColor,
    selectCustomerConfig,
} from './slice';
import {
    Capacity,
    filterTechnicalDetails,
    getDataVolumeWithOrWithoutDiscounts,
    getFromArrayWithRunTimeEnd,
    getFromArrayWithRunTimeStart,
    getMonthlyPriceWithOrWithoutDiscounts,
} from '@vfde-sails/glados-v2';
import { roundToTwoDecimal } from '@vfde-sails/utils';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';

// #region Hardware selectors

/**
 * Selects the HardwareDetailGroup query selector
 */
const createHardwareDetailGroupSelector = createAppSelector(
    [
        selectCustomerConfig,
    ],
    customerConfig => getHardwareDetailGroup.select({
        subscriptionId: customerConfig.subscriptionId,
    }),
);

/**
 * Selects the HardwareDetailGroup query
 */
const selectHardwareDetailGroupQuery = createAppSelector(
    [
        state => state,
        createHardwareDetailGroupSelector,
    ],
    (state, selector) => selector(state),
);

/**
 * Selects the full device
 */
const selectDevice = createAppSelector(
    [
        selectHardwareDetailGroupQuery,
    ],
    query => query?.data || null,
);

/**
 * Selects the device atomics
 */
const selectAtomicDevices = createAppSelector(
    [
        selectDevice,
    ],
    device => device?.atomics || [],
);

/**
 * Selects the hardwareIds of each atomic
 */
const selectAtomicIds = createAppSelector(
    [
        selectAtomicDevices,
    ],
    atomics => atomics.map(atomic => atomic.atomicId),
);

/**
 * Selects the atomicDevice based on the current atomicId in the customerConfig
 */
const selectAtomicDevice = createAppSelector(
    [
        selectDevice,
        selectCustomerConfig,
    ],
    (
        device,
        customerConfig,
    ) => {
        if (!device) {
            return null;
        }

        const { atomicId } = customerConfig;

        return device.atomics
            .find(atomic => atomicId ? atomic.atomicId === atomicId : atomic.isDefault)
            || null
        ;
    },
);

/**
 * Selects the available capacities for the current color
 * based on the customerConfig
 */
const selectAvailableCapacitiesForColor = createAppSelector(
    [
        selectDevice,
        selectCurrentColor,
    ],
    (device, currentColor) => {
        if (!device) {
            return [];
        }

        if (!currentColor) {
            // No idea in which case capacities could be undefined
            return device.availableCapacities!;
        }

        const items: Capacity[] = [];

        for (const atomic of device.atomics) {
            if (atomic.color.displayLabel === currentColor) {
                items.push(atomic.capacity);
            }
        }

        return items;
    },
);

/**
 * Selects the capacity object by the given sortValue
 */
const selectCapacityBySortValue = createAppSelector(
    [
        selectDevice,
        (_state, sortValue: Capacity['sortValue']) => sortValue,
    ],
    (device, sortValue) => {
        if (!device) {
            return null;
        }

        for (const capacity of device.availableCapacities!) {
            if (capacity.sortValue === sortValue) {
                return capacity;
            }
        }

        return null;
    },
);

/**
 * Selects all available capacities merged with the two additional properties
 * `hardwareOnlyPrice` and `hardwareWithTariffOneTimePrice` to be used for the OptionPicker
 */
const selectCapacityOptions = createAppSelector(
    [
        selectDevice,
    ],
    device => {
        if (!device?.availableCapacities) {
            return [];
        }

        return device.availableCapacities.map(capacity => {
            const { atomics } = device;
            const atomicForCapacity = atomics.find(atomic => atomic.capacity.displayLabel === capacity.displayLabel)!;

            const {
                priceHardwareOnly,
                priceHardwareOneTimeWithTariff,
            } = atomicForCapacity;

            return {
                ...capacity,
                priceHardwareOnly,
                priceHardwareOneTimeWithTariff,
            };
        }).sort((capacityA, capacityB) => capacityA.sortValue - capacityB.sortValue);
    },
);

/**
 * Selects all available colors
 */
const selectColorOptions = createAppSelector(
    [
        selectDevice,
    ],
    device => device?.availableColors || [],
);

/**
 * Selects the images for the current atomic
 */
const selectAtomicImages = createAppSelector(
    [
        selectAtomicDevice,
    ],
    atomic => atomic?.imageUrls || [],
);

/**
 * Selects the connectivity attribute (cellular) of the device
 */
const selectConnectivity = createAppSelector(
    [
        selectDevice,
    ],
    device => device?.connectivity || null,
);

/**
 * Selects the technical details object
 */
const selectTechnicalDetails = createAppSelector(
    [
        selectAtomicDevice,
    ],
    atomicDevice => {
        if (!atomicDevice) {
            return [];
        }

        const attributeGroupIdWhitelist = Object.keys(window[ADDITIONAL_PAGE_OPTIONS].technicalDetails.icons);
        const technicalDetails = filterTechnicalDetails(attributeGroupIdWhitelist, atomicDevice.technicalDetails);

        return technicalDetails;
    },
);

const selectDeliveryScope = createAppSelector(
    selectAtomicDevice,
    atomicDevice => atomicDevice?.deliveryScope || [],
);

// #endregion

// #region Tariff selectors

/**
 * Selects the TariffWithHardware query selector
 */
const createTariffWithHardwareSelector = createAppSelector(
    [
        selectAtomicIds,
        selectCustomerConfig,
    ],
    (atomicIds, customerConfig) => getTariffWithHardware.select({
        atomicIds,
        isTauschbonus: customerConfig.isTauschbonus,
    }),
);

/**
 * Returns the TariffWithHardware query
 */
const selectTariffWithHardwareQuery = createAppSelector(
    [
        state => state,
        createTariffWithHardwareSelector,
    ],
    (state, selector) => selector(state),
);

/**
 * Selects the tariff based on the current atomicId in the customerConfig
 */
const selectTariffOptions = createAppSelector(
    [
        selectCustomerConfig,
        selectTariffWithHardwareQuery,
    ],
    (customerConfig, tariffWithhardwareQuery) => {
        const tariffs = tariffWithhardwareQuery.data
            ?.find(tariffData => tariffData.hardware?.atomicId === customerConfig.atomicId)
            ?.tariffs
            || []
        ;

        return tariffs;
    },
);

/**
 * Selects the tariff based on the current atomicId in the customerConfig
 */
const selectTariff = createAppSelector(
    [
        selectCustomerConfig,
        selectTariffWithHardwareQuery,
    ],
    (customerConfig, tariffWithhardwareQuery) => {
        const tariff = tariffWithhardwareQuery.data
            ?.find(tariffData => tariffData.hardware?.atomicId === customerConfig.atomicId)?.tariffs
            .find(t => t.tariffId === customerConfig.subscriptionId)
            || null
        ;

        return tariff;
    },
);

/**
 * Selects the data volume of the tariff
 */
const selectDataVolume = createAppSelector(
    [
        selectTariff,
    ],
    tariff => {
        if (!tariff) {
            return null;
        }

        const dataVolume = getDataVolumeWithOrWithoutDiscounts(
            tariff.dataVolumeWithoutDiscounts,
            tariff.dataVolumeWithDiscounts,
        );

        return dataVolume;
    },
);

// #endregion

// #region API status related selectors

/**
 * Selects the combined `isLoading` state for all RTK queries
 */
const selectIsLoading = createAppSelector(
    [
        selectHardwareDetailGroupQuery,
        selectTariffWithHardwareQuery,
    ],
    (hardwareDetailGroupQuery, tariffWithHardwareQuery) =>
        hardwareDetailGroupQuery.isLoading || tariffWithHardwareQuery.isLoading,
);

/**
 * Selects the combined `isError` state for all RTK queries
 */
const selectIsError = createAppSelector(
    [
        selectHardwareDetailGroupQuery,
        selectTariffWithHardwareQuery,
    ],
    (hardwareDetailGroupQuery, tariffWithHardwareQuery) =>
        hardwareDetailGroupQuery.isError || tariffWithHardwareQuery.isError,
);

// #endregion

// #region OneTime Price selectors

/**
 * Selects the hardware onetime price based on the 'isHardwareOnly' state in the customerConfig
 */
const selectHardwareOneTimePrice = createAppSelector(
    [
        selectAtomicDevice,
        selectCustomerConfig,
    ],
    (atomicDevice, customerConfig) => {
        if (!atomicDevice) {
            return null;
        }

        const {
            priceHardwareOnly: hardwareOnlyPrice,
            priceHardwareOneTimeWithTariff: hardwareWithTariffOneTimePrice,
        } = atomicDevice;

        return customerConfig.isHardwareOnly
            ? hardwareOnlyPrice
            : hardwareWithTariffOneTimePrice
        ;
    },
);

// #endregion

// #region Monthly Price selectors

/**
 * Selects the monthly start tariff price without discounts
 */
const selectMonthlyTariffPriceWithoutDiscounts = createAppSelector(
    selectTariff,
    tariff => tariff?.priceMonthlyTariffWithoutDiscounts || null,
);

/**
 * Selects the monthly start tariff price with discounts
 */
const selectMonthlyTariffPriceWithDiscounts = createAppSelector(
    selectTariff,
    tariff => tariff?.priceMonthlyTariffWithDiscounts || null,
);

/**
 * Selects the monthly start tariff price
 */
const selectMonthlyTariffPrice = createAppSelector(
    [
        selectMonthlyTariffPriceWithoutDiscounts,
        selectMonthlyTariffPriceWithDiscounts,
    ],
    (monthlyTariffPriceWithoutDiscounts, monthlyTariffPriceWithDiscounts) => {
        if (!monthlyTariffPriceWithoutDiscounts) {
            return null;
        }

        const monthlyTariffPrice = getMonthlyPriceWithOrWithoutDiscounts(
            monthlyTariffPriceWithoutDiscounts,
            monthlyTariffPriceWithDiscounts,
            getFromArrayWithRunTimeStart,
        );

        return monthlyTariffPrice;
    },
);

/**
 * Selects the monthly end tariff price
 */
const selectMonthlyEndTariffPrice = createAppSelector(
    [
        selectMonthlyTariffPriceWithoutDiscounts,
        selectMonthlyTariffPriceWithDiscounts,
    ],
    (monthlyTariffPriceWithoutDiscounts, monthlyTariffPriceWithDiscounts) => {
        if (!monthlyTariffPriceWithoutDiscounts) {
            return null;
        }

        const monthlyEndTariffPrice = getMonthlyPriceWithOrWithoutDiscounts(
            monthlyTariffPriceWithoutDiscounts,
            monthlyTariffPriceWithDiscounts,
            getFromArrayWithRunTimeEnd,
        );

        return monthlyEndTariffPrice;
    },
);

/**
 * Selects the monthly insurance price
 */
const selectMonthlyInsurancePrice = createAppSelector(
    [
        selectCustomerConfig,
        selectTariff,
    ],
    (customerConfig, tariff) => {
        if (!customerConfig.insurance || !tariff) {
            return null;
        }

        const { priceInsurance } = tariff;

        return priceInsurance || null;
    },
);

/**
 * Selects the total monthly start price
 */
const selectMonthlyPriceToPay = createAppSelector(
    [
        selectMonthlyTariffPrice,
        selectMonthlyInsurancePrice,
    ],
    (monthlyTariffPrice, monthlyInsurancePrice) => {
        if (!monthlyTariffPrice) {
            return null;
        }

        return {
            ...monthlyTariffPrice,
            gross: roundToTwoDecimal(monthlyTariffPrice.gross + (monthlyInsurancePrice?.gross || 0)),
        };
    },
);

/**
 * Selects the total monthly end price
 */
const selectMonthlyEndPriceToPay = createAppSelector(
    [
        selectMonthlyEndTariffPrice,
        selectMonthlyInsurancePrice,
    ],
    (monthlyTariffPrice, monthlyInsurancePrice) => {
        if (!monthlyTariffPrice) {
            return null;
        }

        return {
            ...monthlyTariffPrice,
            gross: roundToTwoDecimal(monthlyTariffPrice.gross + (monthlyInsurancePrice?.gross || 0)),
        };
    },
);

/**
 * Selects the monthly strike price
 */
const selectMonthlyStrikePrice = createAppSelector(
    [
        selectTariff,
        selectCustomerConfig,
    ],
    (tariff, customerConfig) => {
        if (!tariff) {
            return null;
        }

        return customerConfig.insurance
            ? tariff.priceMonthlyStrikePriceWithInsurance
            : tariff.priceMonthlyStrikePrice
        ;
    },
);

/**
 * Selects a boolean that indicates if the monthly price has a stairway (increases after x. months)
 */
const selectHasStairway = createAppSelector(
    [
        selectCustomerConfig,
        selectMonthlyTariffPrice,
        selectMonthlyEndTariffPrice,
    ],
    (customerConfig, monthlyTariffPrice, monthlyEndTariffPrice) =>
        !customerConfig.isHardwareOnly
        && !!(
            monthlyTariffPrice
            && monthlyEndTariffPrice
            && monthlyTariffPrice !== monthlyEndTariffPrice
        ),
);

// #endregion

// #region Misc selectors

/**
 * Select the pageview tracking data
 */
const selectPageViewTrackingData = createAppSelector(
    [
        selectTariff,
        selectAtomicDevice,
        selectCustomerConfig,
        selectMonthlyPriceToPay,
    ],
    (subscription, atomic, customerConfig, monthlyPriceToPay) => ({
        subscription,
        atomic,
        customerConfig,
        monthlyPriceToPay,
    }),
);

// #endregion

export {
    selectIsLoading,
    selectIsError,
    selectDevice,
    selectAtomicDevices,
    selectAtomicIds,
    selectAtomicDevice,
    selectCapacityOptions,
    selectColorOptions,
    selectAvailableCapacitiesForColor,
    selectTariffOptions,
    selectTariff,
    selectPageViewTrackingData,
    selectCapacityBySortValue,
    selectHardwareOneTimePrice,
    selectMonthlyPriceToPay,
    selectMonthlyEndPriceToPay,
    selectMonthlyStrikePrice,
    selectHasStairway,
    selectDataVolume,
    selectAtomicImages,
    selectConnectivity,
    selectTechnicalDetails,
    selectDeliveryScope,
};
